import React, { CSSProperties, FunctionComponent, ReactNode } from 'react';

import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';

import PaletteProvider from '@gojob/ui-bits/styles/PaletteProvider';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Overlay from './Overlay';
import useAspectRatioStyles from './useAspectRatioStyles';
import useHotspotStyles from './useHotspotStyles';

export type Hotspot = {
  x?: number;
  y?: number;
};

export type BackdropImageProps = {
  loading?: 'lazy' | 'eager';
  hideOverlay?: boolean;
  image: GatsbyImageProps['image'];
  hotspot?: Hotspot;
  alt?: string;
  aspectRatio?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

const useStyles = () => {
  const { spacing } = useTheme();

  return {
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-end',
    },
    background: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    content: {
      position: 'relative',
      padding: spacing(4),
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'stretch',
      '& > *': {
        zIndex: 1,
      },
    },
  } as const;
};

const BackdropImage: FunctionComponent<BackdropImageProps> = (props) => {
  const {
    loading = 'lazy',
    image,
    alt = '',
    hideOverlay,
    hotspot,
    aspectRatio,
    className,
    style,
    children,
  } = props;

  const classes = useStyles();
  const ratioClass = useAspectRatioStyles(aspectRatio);
  const hotspotStyle = useHotspotStyles(hotspot);

  return (
    <PaletteProvider variant="primary">
      <div css={[classes.root, ratioClass]} className={className} style={style}>
        <GatsbyImage
          loading={loading}
          imgStyle={hotspotStyle}
          style={classes.background}
          image={image}
          alt={alt}
        />
        <div css={classes.content}>
          {hideOverlay ? null : <Overlay />}
          {children}
        </div>
      </div>
    </PaletteProvider>
  );
};

export default BackdropImage;
